import { tr } from '@/lib/i18n-core';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import type { SupportAccessLogDto } from '@his/shared';
import { get } from '@/lib/api';
import { dateTime } from '@/lib/utils';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, SkeletonText } from '@/components/ui/primitives';

export function SupportAccessCard() {
  const log = useQuery({
    queryKey: ['support-access'],
    queryFn: () => get<SupportAccessLogDto>('/support-access'),
    staleTime: 60_000,
    retry: false,
  });
  const entries = log.data?.entries ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" aria-hidden /> {tr('admin', 'support_access')}
        </CardTitle>
        <CardDescription>
          {tr('admin', 'only_the_platform_owner_can_ever_sign_in_to')}
          {log.data && !log.data.complete && ' This server shows the last 20 sessions; the full history is available from support.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        {log.isLoading && <SkeletonText lines={2} />}
        {log.isError && <p className="text-muted-foreground">{tr('admin', 'the_access_log_could_not_be_loaded')}</p>}
        {log.data && entries.length === 0 && <p className="text-muted-foreground">{tr('admin', 'the_owner_has_never_signed_in_to_this_facility')}</p>}
        {entries.length > 0 && (
          <ul className="divide-y">
            {entries.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                <div className="min-w-0">
                  <p className="font-medium">
                    {entry.by}
                    {entry.userName ? <span className="font-normal text-muted-foreground"> {tr('admin', 'as')} {entry.userName}</span> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">{entry.reason}</p>
                </div>
                <div className="text-end text-xs text-muted-foreground">
                  <p>{dateTime(entry.startedAt)}</p>
                  <p>{entry.active ? <Badge variant="warning">{tr('admin', 'open_now')}</Badge> : entry.endedAt ? tr('admin', 'ended_x', { endedAt: dateTime(entry.endedAt) }) : ''}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
